// First run (wave 12, account track): the house rules and playtime in plain words, a name, what you like to play,
// a few creators to follow, and friends from X. Every step after the rules can be skipped; nothing here is a wall.

import { api, ApiError } from "../api.ts";
import { getCatalogue, type Catalogue } from "../catalogue.ts";
import { clear, errorBox, h } from "../dom.ts";
import { navigate, setTitle } from "../router.ts";
import { coverUrl } from "../rows.ts";
import { signedIn } from "../session.ts";
import { accountStyles, ARCHETYPES, artWall, GENRES, getFullMe, MAX_ARCHETYPES, MAX_GENRES, patchMe, type FullMe } from "./me-kit.ts";
import { safeNext } from "./signin.ts";
import { WRITE_ONLY_SINCE } from "../zones/creators/follow.ts";

/** The house rules, in the words players read. Changing them means bumping accounts' TERMS_VERSION. */
export const HOUSE_RULES: [string, string, string][] = [
  ["♥", "Be kind", "No harassment, hate or threats. Disagree with the game, not the person."],
  ["⚑", "Play fair", "No cheating other players, no scams, no pretending to be someone else."],
  ["☂", "Keep it safe", "Players are 13 and up. Nothing sexual, nothing that puts anyone at risk. Report what's wrong and we'll look."],
  ["✎", "Games belong to their creators", "We list games and link to them; the creators own them. Creators can claim or remove their listing at any time."],
];

const STEPS = ["rules", "name", "taste", "creators", "friends"] as const;
type Step = (typeof STEPS)[number];

/** Creators to suggest: the ones with the most games in the genres someone picked (or overall), with cover art. */
export function suggestCreators(cat: Pick<Catalogue, "games">, genres: string[], n = 6): { id: string; name: string; games: number; cover: string | null }[] {
  const by = new Map<string, { id: string; name: string; games: number; score: number; cover: string | null }>();
  for (const g of cat.games) {
    const c = g.creator;
    if (!c?.id || g.firstParty) continue;
    const e = by.get(c.id) ?? { id: c.id, name: c.name, games: 0, score: 0, cover: null };
    e.games++;
    e.score += genres.includes(g.genre) ? 3 : 1;
    e.cover ??= coverUrl(g, "thumb");
    by.set(c.id, e);
  }
  return [...by.values()]
    .filter((e) => e.cover && e.games >= 1)
    .sort((a, b) => b.score - a.score || b.games - a.games || a.name.localeCompare(b.name))
    .slice(0, n)
    .map(({ id, name, games, cover }) => ({ id, name, games, cover }));
}

export async function welcomePage(_p: unknown, query: URLSearchParams): Promise<HTMLElement> {
  setTitle("Welcome");
  accountStyles();
  const next = safeNext(query.get("next"));
  if (!signedIn()) {
    navigate(`/signin?next=${encodeURIComponent("/welcome")}`, true);
    return h("div");
  }
  const body = h("div", { class: "acct-welcome" }, h("p", { class: "dim" }, "Getting your space ready…"));
  const root = h("div", { class: "acct" }, artWall("dawn"), body);
  let me: FullMe;
  try {
    me = await getFullMe();
  } catch (e) {
    clear(body, errorBox(e));
    return root;
  }
  const want = query.get("step") as Step | null;
  const rulesDone = !!me.terms?.version && me.terms.version === me.terms.current;
  let at = Math.max(0, STEPS.indexOf(want && STEPS.includes(want) ? want : rulesDone ? "name" : "rules"));
  const taste = { archetypes: [...(me.taste?.archetypes ?? [])], genres: [...(me.taste?.genres ?? [])] };

  const finish = async (to?: string) => {
    try {
      await patchMe({ onboarded: true });
    } catch {
      /* first run shows again next time; don't trap them here */
    }
    navigate(to ?? (next === "/" ? "/me" : next), true);
  };
  const go = (i: number) => {
    at = i;
    render();
    window.scrollTo(0, 0);
  };
  const progress = () => h("ol", { class: "acct-steps", "aria-label": `Step ${at + 1} of ${STEPS.length}` }, STEPS.map((_, i) => h("li", { class: i <= at ? "on" : "" })));
  const foot = (primary: HTMLElement, skip: boolean) =>
    h("div", { class: "acct-foot" }, skip ? h("button", { class: "acct-link", type: "button", onclick: () => (at + 1 < STEPS.length ? go(at + 1) : void finish()) }, "Skip for now") : h("span"), primary);

  function render(): void {
    const step = STEPS[at]!;
    clear(body, progress(), h("div", { class: "acct-enter" }, ...VIEWS[step]()));
  }

  const VIEWS: Record<Step, () => (HTMLElement | null)[]> = {
    rules: () => {
      const err = h("div", { "aria-live": "polite" });
      const agree = h("input", { type: "checkbox", id: "agree" }) as HTMLInputElement;
      let playtime = me.consent.playtime;
      const sw = h("button", { class: "acct-switch", role: "switch", type: "button", "aria-checked": String(playtime), "aria-label": "Count my playtime" });
      sw.addEventListener("click", () => {
        playtime = !playtime;
        sw.setAttribute("aria-checked", String(playtime));
      });
      const cont = h("button", { class: "btn", type: "button" }, "I'm in");
      cont.addEventListener("click", async () => {
        if (!agree.checked) return clear(err, h("p", { class: "error", role: "alert" }, "Tick the box to agree to the house rules."));
        cont.setAttribute("disabled", "");
        try {
          me = await patchMe({ acceptTerms: me.terms?.current, consent: { playtime } });
          go(at + 1);
        } catch (e) {
          cont.removeAttribute("disabled");
          clear(err, errorBox(e));
        }
      });
      return [
        h("p", { class: "kicker" }, "Welcome to Banger"),
        h("h1", null, "You're in. ", h("span", { class: "hot" }, "Here's how we play.")),
        h("p", { class: "lede" }, "Four house rules, and one thing we count. Then we'll set up your space."),
        h("div", { class: "acct-plain" }, HOUSE_RULES.map(([ic, t, d]) => h("div", null, h("span", { class: "ic", "aria-hidden": "true" }, ic), h("div", null, h("b", null, t), h("p", null, d))))),
        h(
          "div",
          { class: "acct-toggle" },
          h(
            "div",
            null,
            h("b", null, "Count my playtime"),
            h("p", null, "When you play in the Banger app, we count the minutes you actually play each game (not idle time). It puts your recent games first, and when creators get paid, it decides how they share what Banger earns, so the games you play most get the most."),
            h("p", null, me.ageBand === "13-17" ? "Only you can see it. You can switch it off any time in Settings." : "Nobody sees your minutes unless you let them. You can switch it off any time in Settings."),
          ),
          sw,
        ),
        h("label", { class: "acct-check", for: "agree" }, agree, h("span", null, "I agree to the house rules.")),
        err,
        foot(cont, false),
      ];
    },

    name: () => {
      const err = h("div", { "aria-live": "polite" });
      const display = h("input", { id: "display", type: "text", maxlength: 40, value: me.displayName ?? "", placeholder: "What friends call you", autocomplete: "nickname" }) as HTMLInputElement;
      const handle = h("input", { id: "handle", type: "text", maxlength: 20, value: me.handle ?? "", placeholder: "yourname", autocomplete: "username", spellcheck: "false" }) as HTMLInputElement;
      const cont = h("button", { class: "btn", type: "button" }, "Continue");
      cont.addEventListener("click", async () => {
        const patch: Record<string, unknown> = {};
        const d = display.value.trim();
        const hd = handle.value.trim().replace(/^@/, "");
        if (d !== (me.displayName ?? "")) patch.displayName = d || null;
        if (hd && hd !== me.handle) patch.handle = hd;
        if (!Object.keys(patch).length) return go(at + 1);
        cont.setAttribute("disabled", "");
        try {
          me = await patchMe(patch);
          go(at + 1);
        } catch (e) {
          cont.removeAttribute("disabled");
          const code = e instanceof ApiError ? e.code : "";
          clear(err, code === "handle_taken" ? h("p", { class: "error" }, `@${hd} is taken. Try another.`) : code === "invalid_handle" ? h("p", { class: "error" }, "Handles are 3–20 letters, digits or _, starting with a letter.") : errorBox(e));
        }
      });
      return [
        h("p", { class: "kicker" }, "Step 2 · You"),
        h("h1", null, "What should we call you?"),
        h("p", { class: "lede" }, "Your name shows on your profile and to friends. Your handle is your address: banger.sh/u/yourname."),
        h(
          "div",
          { class: "acct-card" },
          h("div", { class: "field" }, h("label", { for: "display" }, "Name"), display),
          h("div", { class: "field" }, h("label", { for: "handle" }, "Handle"), handle, h("p", { class: "fine" }, "3–20 letters, digits or _, starting with a letter.")),
          err,
        ),
        foot(cont, true),
      ];
    },

    taste: () => {
      const picksA = h("div", { class: "acct-picks", role: "group", "aria-label": "Kinds of player" });
      const picksG = h("div", { class: "acct-chips", role: "group", "aria-label": "Genres" });
      const count = h("p", { class: "dim", "aria-live": "polite" });
      const paint = () => {
        clear(
          picksA,
          ARCHETYPES.map((a) => {
            const on = taste.archetypes.includes(a.id);
            const b = h("button", { class: "acct-pick", type: "button", "aria-pressed": String(on), disabled: !on && taste.archetypes.length >= MAX_ARCHETYPES }, h("b", null, a.name), h("span", null, a.line));
            b.style.setProperty("--h", String(a.hue));
            b.addEventListener("click", () => {
              taste.archetypes = on ? taste.archetypes.filter((x) => x !== a.id) : [...taste.archetypes, a.id];
              paint();
            });
            return b;
          }),
        );
        clear(
          picksG,
          GENRES.map(([id, name]) => {
            const on = taste.genres.includes(id);
            const b = h("button", { class: "acct-chip", type: "button", "aria-pressed": String(on), disabled: !on && taste.genres.length >= MAX_GENRES }, name);
            b.addEventListener("click", () => {
              taste.genres = on ? taste.genres.filter((x) => x !== id) : [...taste.genres, id];
              paint();
            });
            return b;
          }),
        );
        count.textContent = taste.archetypes.length ? `${taste.archetypes.length} of ${MAX_ARCHETYPES} picked` : `Pick up to ${MAX_ARCHETYPES}.`;
      };
      paint();
      const err = h("div", { "aria-live": "polite" });
      const cont = h("button", { class: "btn", type: "button" }, "Continue");
      cont.addEventListener("click", async () => {
        cont.setAttribute("disabled", "");
        try {
          me = await patchMe({ taste });
          go(at + 1);
        } catch (e) {
          cont.removeAttribute("disabled");
          clear(err, errorBox(e));
        }
      });
      return [
        h("p", { class: "kicker" }, "Step 3 · Taste"),
        h("h1", null, "What kind of player are you?"),
        h("p", { class: "lede" }, "Pick the corners of Banger that sound like you. We'll put those games first, and our curators will know where you hang out."),
        count,
        picksA,
        h("h2", { style: { fontSize: "20px", margin: "28px 0 0" } }, "And the genres you can't resist"),
        picksG,
        err,
        foot(cont, true),
      ];
    },

    creators: () => {
      const list = h("div", { class: "acct-people" }, h("p", { class: "dim" }, "Finding creators…"));
      const followed = new Set<string>();
      const err = h("div", { "aria-live": "polite" });
      void getCatalogue()
        .then((cat) => {
          const people = suggestCreators(cat, taste.genres);
          if (!people.length) return go(at + 1);
          clear(
            list,
            people.map((p) => {
              const b = h("button", { class: "btn small ghost", type: "button", "aria-pressed": "false" }, "Follow");
              b.addEventListener("click", () => {
                const on = !followed.has(p.id);
                if (on) followed.add(p.id);
                else followed.delete(p.id);
                b.setAttribute("aria-pressed", String(on));
                b.textContent = on ? "Following" : "Follow";
                b.className = on ? "btn small" : "btn small ghost";
              });
              const face = h("div", { class: "face" });
              face.style.backgroundImage = `url("${p.cover}")`;
              return h("div", { class: "acct-person" }, face, h("div", null, h("b", null, p.name), h("span", null, `${p.games} ${p.games === 1 ? "game" : "games"}`)), b);
            }),
          );
        })
        .catch((e) => clear(list, errorBox(e)));
      const cont = h("button", { class: "btn", type: "button" }, "Continue");
      cont.addEventListener("click", async () => {
        if (followed.size) {
          cont.setAttribute("disabled", "");
          try {
            const ts = Date.now();
            await api("accounts", "/me/sync", { body: { marks: [...followed].map((target) => ({ kind: "follow", target, on: true, updatedAt: ts })), since: WRITE_ONLY_SINCE } });
          } catch (e) {
            cont.removeAttribute("disabled");
            return clear(err, errorBox(e));
          }
        }
        go(at + 1);
      });
      return [
        h("p", { class: "kicker" }, "Step 4 · People"),
        h("h1", null, "Follow a few creators"),
        h("p", { class: "lede" }, "When someone you follow ships a game or an update, you'll hear about it first."),
        list,
        err,
        foot(cont, true),
      ];
    },

    friends: () => {
      const done = h("button", { class: "btn", type: "button", onclick: () => void finish() }, "Take me in");
      return [
        h("p", { class: "kicker" }, "Step 5 · Friends"),
        h("h1", null, "Bring your people"),
        h("p", { class: "lede" }, "Already follow players and creators on X? Connect your account and we'll reconnect you with the ones who are here, and tell you when the rest arrive."),
        h(
          "div",
          { class: "acct-plain" },
          h("div", null, h("span", { class: "ic", "aria-hidden": "true" }, "𝕏"), h("div", null, h("b", null, "Find your friends from X"), h("p", null, "You prove the account is yours with one post, then upload your follower list from X's data export. We never log in to X, and we keep only scrambled IDs."), h("div", { class: "acct-row" }, h("button", { class: "btn ghost", type: "button", onclick: () => void finish("/me/settings#connected") }, "Connect X")))),
        ),
        foot(done, false),
      ];
    },
  };

  render();
  return root;
}
